import styled from "styled-components";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUpRightFromSquare, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import portrait from "../../img/imagepriyanshu.jpeg";
import { profile } from "../../data/profile";
import { useReducedMotion } from "../../hooks/useReducedMotion";

function Hero() {
  const reduced = useReducedMotion();
  const initial = reduced ? false : { opacity: 0, y: 24 };
  const animate = reduced ? undefined : { opacity: 1, y: 0 };

  return (
    <StyledHero id="home">
      <HeroCopy initial={initial} animate={animate} transition={{ duration: 0.65 }}>
        <Eyebrow>{profile.role} · {profile.location}</Eyebrow>
        <h1>
          I build full-stack products that feel <em>effortless.</em>
        </h1>
        <p>{profile.summary}</p>
        <HeroButtons>
          <Primary href="#work">
            View projects <FontAwesomeIcon icon={faArrowDown} />
          </Primary>
          <TextLink href="/resume.pdf" target="_blank" rel="noreferrer">
            View resume <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </TextLink>
          <TextLink href="#contact">
            Contact me <FontAwesomeIcon icon={faEnvelope} />
          </TextLink>
        </HeroButtons>
      </HeroCopy>
      <PortraitWrap
        initial={reduced ? false : { opacity: 0, scale: 0.96 }}
        animate={reduced ? undefined : { opacity: 1, scale: 1 }}
        transition={{ duration: 0.75, delay: 0.1 }}
      >
        <Portrait src={portrait} alt={`Portrait of ${profile.name}`} />
        <Availability>
          <i /> Available for opportunities
        </Availability>
      </PortraitWrap>
    </StyledHero>
  );
}

const StyledHero = styled.section`
  max-width: 1240px;
  margin: auto;
  min-height: 660px;
  padding: 88px 28px 76px;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  align-items: center;
  gap: 80px;

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
    gap: 50px;
    padding-top: 60px;
    min-height: auto;
  }
`;

const HeroCopy = styled(motion.div)`
  max-width: 720px;

  h1 {
    font-size: clamp(2.6rem, 6.4vw, 5.4rem);
    letter-spacing: -0.03em;
    line-height: 1.05;
    margin: 17px 0 27px;
    color: var(--text-primary);
    font-weight: 700;
  }

  em {
    font-family: Georgia, serif;
    font-weight: 400;
    font-style: normal;
    color: var(--accent);
  }

  p {
    max-width: 560px;
    color: var(--text-secondary);
    font-size: 1.08rem;
    line-height: 1.7;
    margin: 0;
  }
`;

const Eyebrow = styled.p`
  font-size: 0.72rem !important;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--accent) !important;
  font-weight: 800;
  margin: 0;
`;

const HeroButtons = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 28px;
  margin-top: 36px;
`;

const Primary = styled.a`
  background: var(--text-primary);
  color: var(--bg);
  text-decoration: none;
  padding: 15px 20px;
  font-size: 0.84rem;
  font-weight: 700;
  border-radius: 4px;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: var(--accent);
    color: #fff;
    transform: translateY(-2px);
  }

  svg {
    margin-left: 13px;
  }
`;

const TextLink = styled.a`
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 700;
  border-bottom: 1px solid var(--text-primary);
  padding-bottom: 4px;
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: var(--accent);
    border-color: var(--accent);
  }

  svg {
    margin-left: 8px;
  }
`;

const PortraitWrap = styled(motion.div)`
  position: relative;
  justify-self: end;
  width: min(100%, 360px);

  @media (max-width: 780px) {
    justify-self: start;
    width: min(100%, 320px);
  }
`;

const Portrait = styled.img`
  display: block;
  width: 100%;
  height: 430px;
  object-fit: cover;
  object-position: center top;
  filter: grayscale(35%);
  border-radius: 6px;
  box-shadow: var(--shadow);

  @media (max-width: 460px) {
    height: 380px;
  }
`;

const Availability = styled.div`
  position: absolute;
  bottom: -17px;
  left: -18px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  padding: 12px 15px;
  border-radius: 4px;
  box-shadow: var(--shadow);
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;

  i {
    display: inline-block;
    width: 8px;
    height: 8px;
    background: var(--success);
    border-radius: 50%;
    margin-right: 7px;
  }

  @media (max-width: 460px) {
    left: 0;
  }
`;

export default Hero;
